import { useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useLocalizationStore } from '@stores/localizationStore';
import { getStringBundle } from '@services/localizationService';

/**
 * Translation hook — returns a `t()` function that resolves localisation keys.
 *
 * The string bundle is fetched from the localisation-service (public endpoint —
 * no session required) and cached. Changing the language in the store
 * triggers a new fetch.
 *
 * Fallback chain: bundle value → caller-supplied fallback → key itself.
 */
export function useTranslation() {
  const language = useLocalizationStore((s) => s.language);

  const { data: strings, isLoading } = useQuery({
    queryKey: ['localization', 'bundle', language],
    queryFn: () => getStringBundle(language),
    staleTime: Infinity,
    gcTime: 30 * 60 * 1000,
  });

  const t = useCallback(
    (key: string, fallback?: string): string => {
      return strings?.[key] ?? fallback ?? key;
    },
    [strings],
  );

  return { t, isLoading };
}
